import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  addPrefix,
  addSuffix,
  commasForNumbers,
  numberToCurrencyFormatter,
  numFormatterWithSuffix,
  roundNumber,
} from './helper';
import { valueWithPercentage } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { getLegendItem } from '../../pages/UtilizationRx/utilizationRxConstants';
import { partitionValues } from '@HomaHealth/ui-component-library/lib/_AIQ-components/molecules/PartitionCard';
import {
  SvgMaleIcon,
  SvgFemaleIcon,
  SvgTransgenderIcon,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React from 'react';

export interface TotalEnrolled {
  totalCount: string;
  dependents: string;
  employees: string;
}

export interface TotalCost {
  totalCost: string;
  totalRx: string;
  totalMedical: string;
}

export interface CostValues {
  rx: string;
  medical: string;
  total: string;
}

export interface MemberValues {
  members: string;
  employees: string;
  dependents: string;
}

export interface PMPM {
  pmpm: string;
  pmpmPercentage: string;
  increment: boolean;
}

export interface PEPM {
  pepm: string;
  pepmPercentage: string;
  increment: boolean;
}

export interface TotalSpend {
  employeeSpend: string;
  dependentSpend: string;
  totalSpend: string;
}

export interface AverageAge {
  allMembers: string;
  dependent: string;
  employee: string;
}

export interface Gender {
  allMale: string;
  allFemale: string;
  maleEmployee: string;
  femaleEmployee: string;
  maleDependent: string;
  femaleDependent: string;
}

interface RiskStratificationDataType {
  rowid: string;
  columnid: string;
  displayvalue: number | string;
  colorrangelabel: string;
  uniqueMemberIds: string[];
  brLabel: string;
  handleClick: () => any;
  link?: string;
}

export interface RiskStratificationChartType {
  riskStratificationColumns: { id: string; label: string }[];
  riskStratificationRows: { id: string; label: string }[];
  riskStratificationData: RiskStratificationDataType[];
}

export interface RiskStratificationCumulative {
  tier: string;
  count: number;
  memberIds: string[];
  handleClick: () => any;
}

export interface RiskIndex {
  currentYearIndex: number;
  previousYearIndex: number;
  riskPercentage: number;
  increment: boolean;
  riskStatus: string;
}
export interface MedicalLossRatio {
  current_year_mlr: number | string;
  previous_year_mlr: number | string;
  percentage: number;
  increment: boolean;
}

export interface CareQualityData {
  data: { label: string; value: string; color: string; toolText?: string }[];
  centerLabel: string;
}

export interface CostTrendDataType {
  month: string;
  value: string;
}

export interface CostTrendChartType {
  medicalClaimsData: CostTrendDataType[];
  medicalTotalPaid: CostTrendDataType[];
  medicalPlanPaid: CostTrendDataType[];
  medicalDeductible: CostTrendDataType[];
  medicalCopay: CostTrendDataType[];
  medicalCoinsurance: CostTrendDataType[];

  rxClaims: CostTrendDataType[];
  rxTotalPaid: CostTrendDataType[];
  rxPlanPaid: CostTrendDataType[];
  rxDeductible: CostTrendDataType[];
  rxCopay: CostTrendDataType[];
  rxCoinsurance: CostTrendDataType[];
  xAxisValues?: any[];
}

export interface TitleValueType {
  title: string;
  value: number;
}

export interface CareQualityMemberData {
  totalMetUnmet: number;
  totalMet: number;
  totalUnmet: number;
  totalEnrolles: number;
}

export const formatTotalEnrolled = (data: any) => {
  const totalEnrolled: TotalEnrolled = {
    totalCount: '-',
    dependents: '-',
    employees: '-',
  };
  data?.totalEnrollesCount?.map((item: any) => {
    if (item?.member_role === 'Dependent') {
      totalEnrolled.dependents = item?.total_enrolles_count;
    } else if (item?.member_role === 'Employee') {
      totalEnrolled.employees = item?.total_enrolles_count;
    }
  });
  totalEnrolled.totalCount = (
    Number(totalEnrolled?.dependents) + Number(totalEnrolled?.employees)
  )?.toString();
  return totalEnrolled;
};

export const formatAvgAge = (data: any) => {
  const avgAge: AverageAge = {
    allMembers: '-',
    employee: '-',
    dependent: '-',
  };
  data?.totalEnrollesCount?.map((item: any) => {
    if (item?.member_role === 'Dependent') {
      avgAge.dependent = `${parseInt(item?.avgAge)}`;
    } else if (item?.member_role === 'Employee') {
      avgAge.employee = `${parseInt(item?.avgAge)}`;
    }
  });
  avgAge.allMembers = commasForNumbers(data.avgAge, 0);
  return avgAge;
};

export const formatGender = (data: any) => {
  const gender: Gender = {
    allMale: '-',
    allFemale: '-',
    maleDependent: '-',
    femaleDependent: '-',
    maleEmployee: '-',
    femaleEmployee: '-',
  };
  let totalMale: number = 0;
  let totalFemale = 0;
  data?.totalEnrollesCount?.map((item: any) => {
    if (item?.member_role === 'Dependent') {
      gender.maleDependent = item?.male;
      gender.femaleDependent = item?.female;
    } else if (item?.member_role === 'Employee') {
      gender.maleEmployee = item?.male;
      gender.femaleEmployee = item?.female;
    }
    totalFemale += item?.female;
    totalMale += item.male;
  });
  gender.allFemale = totalFemale.toString();
  gender.allMale = totalMale.toString();
  return gender;
};

export const formatTotalCost = (data: any, decimalDigits: number = 1) => {
  const totalCost: TotalCost = {
    totalCost: '',
    totalRx: '',
    totalMedical: '',
  };

  let total: number = 0;
  data?.totalCost?.map((item: any) => {
    if (item?.claim_type === 'rx') {
      total += item?.totalCost;
      totalCost.totalRx = addPrefix(
        '$',
        numFormatterWithSuffix(item?.totalCost, decimalDigits),
      );
    } else if (item?.claim_type === 'medical') {
      total += item?.totalCost;
      totalCost.totalMedical = addPrefix(
        '$',
        numFormatterWithSuffix(item?.totalCost, decimalDigits),
      );
    }
  });
  totalCost.totalCost = addPrefix(
    '$',
    numFormatterWithSuffix(total, decimalDigits),
  );
  return totalCost;
};

export const formatTotalSpend = (data: any, decimalDigits: number = 1) => {
  const totalSpend: TotalSpend = {
    totalSpend: '',
    employeeSpend: '',
    dependentSpend: '',
  };
  let total: number = 0;
  data.map((curr: any) => {
    total += curr.total_spend;
    if (curr.member_role === 'Employee') {
      totalSpend.employeeSpend = addPrefix(
        '$',
        commasForNumbers(`${curr.total_spend}`, decimalDigits),
      );
    } else if (curr.member_role === 'Dependent') {
      totalSpend.dependentSpend = addPrefix(
        '$',
        commasForNumbers(`${curr.total_spend}`, decimalDigits),
      );
    }
  }, {});
  totalSpend.totalSpend = addPrefix(
    '$',
    commasForNumbers(`${total}`, decimalDigits),
  );
  return totalSpend;
};

export const formatDiseaseTotalCost = (
  data: any,
  decimalDigits: number = 1,
) => {
  const totalCost: TotalCost = {
    totalCost: '',
    totalRx: '',
    totalMedical: '',
  };
  totalCost.totalCost = addPrefix(
    '$',
    numFormatterWithSuffix(
      data.reduce((total: any, obj: any) => total + obj.totalCost, 0),
      decimalDigits,
    ),
  );
  totalCost.totalMedical = addPrefix(
    '$',
    numFormatterWithSuffix(
      data.reduce((total: any, obj: any) => total + obj.medicalCost, 0),
      decimalDigits,
    ),
  );
  totalCost.totalRx = addPrefix(
    '$',
    numFormatterWithSuffix(
      data.reduce((total: any, obj: any) => total + obj.rxCost, 0),
      decimalDigits,
    ),
  );
  return totalCost;
};

export const formatCostValues = (data: any) => {
  const costValues: CostValues = {
    rx: '$0.00',
    medical: '$0.00',
    total: '$0.00',
  };

  let total: number = 0;
  data?.totalCost?.map((item: any) => {
    if (item?.claim_type === 'rx') {
      total += item?.totalCost;
      costValues.rx = '$' + commasForNumbers(item?.totalCost, 2);
    } else if (item?.claim_type === 'medical') {
      total += item?.totalCost;
      costValues.medical = '$' + commasForNumbers(item?.totalCost, 2);
    }
  });
  costValues.total = '$' + commasForNumbers(String(total), 2);
  return costValues;
};
export const formatDiseaseCostValues = (data: any) => {
  const costValues: CostValues = {
    rx: '$0.00',
    medical: '$0.00',
    total: '$0.00',
  };
  costValues.medical =
    '$' +
    commasForNumbers(
      data.reduce((total: any, obj: any) => total + obj.medicalCost, 0),
    );
  costValues.rx =
    '$' +
    commasForNumbers(
      data.reduce((total: any, obj: any) => total + obj.rxCost, 0),
    );
  costValues.total =
    '$' +
    commasForNumbers(
      data.reduce((total: any, obj: any) => total + obj.totalCost, 0),
    );
  return costValues;
};

export const formatMemberValues = (data: any) => {
  const memberValues: MemberValues = {
    members: '0',
    employees: '0',
    dependents: '0',
  };

  let total = 0;

  data?.totalEnrollesCount?.map((item: any) => {
    if (item?.member_role === 'Employee') {
      memberValues.employees = commasForNumbers(item?.total_enrolles_count);
      total += item?.total_enrolles_count;
    } else if (item?.member_role === 'Dependent') {
      memberValues.dependents = commasForNumbers(item?.total_enrolles_count);
      total += item?.total_enrolles_count;
    }
  });
  memberValues.members = commasForNumbers(total.toString());

  return memberValues;
};

export const formatGenderValues = (data: any) => {
  const memberValues: partitionValues[] = [
    {
      title: 'Male',
      value: '0',
      icon: <SvgMaleIcon height="15px" width="15px" />,
    },
    {
      title: 'Female',
      value: '0',
      icon: <SvgFemaleIcon height="15px" width="15px" />,
    },
    {
      title: 'Others',
      value: '0',
      icon: <SvgTransgenderIcon height="15px" width="15px" />,
    },
  ];

  data?.map((item: any) => {
    memberValues[0].value = (
      Number(memberValues[0].value) + item.male
    ).toString();
    memberValues[1].value = (
      Number(memberValues[1].value) + item.female
    ).toString();
    memberValues[2].value = (
      Number(memberValues[2].value) +
      (item.total_enrolles_count - item.male - item.female)
    ).toString();
  });

  return memberValues;
};

export const formatPMPM = (data: any) => {
  const pmpm: PMPM = {
    pmpm: '',
    pmpmPercentage: '',
    increment: false,
  };

  pmpm.pmpm = numberToCurrencyFormatter(data?.pmpm);
  pmpm.pmpmPercentage = addSuffix(
    Math.abs(roundNumber(data?.pmpmPercentage, 1)),
    '%',
  );
  pmpm.increment = data?.pmpmPercentage > 0;
  return pmpm;
};

export const formatPEPM = (data: any) => {
  const pepm: PEPM = {
    pepm: '',
    pepmPercentage: '',
    increment: false,
  };

  pepm.pepm = numberToCurrencyFormatter(data?.pepm);
  pepm.pepmPercentage = addSuffix(
    Math.abs(roundNumber(data?.pepmPercentage, 1)),
    '%',
  );
  pepm.increment = data?.pepmPercentage > 0;
  return pepm;
};

export const formatRiskStratificationCumulative = (data: any) => {
  const riskStratificationCumulative: RiskStratificationCumulative[] = [];

  data?.map((item: any) => {
    const eachValue: RiskStratificationCumulative = {
      tier: '',
      count: 0,
      memberIds: [],
      handleClick: () => {},
    };
    eachValue.tier = item._id;
    eachValue.count = item.summaryCount;
    eachValue.memberIds = item.member_unique_id_list;
    eachValue.handleClick = () => {};
    riskStratificationCumulative.push(eachValue);
  });

  return riskStratificationCumulative;
};

export const formatRiskCost = (data: any, storeMemberIds?: any) => {
  const riskCost: RiskStratificationCumulative[] = [];

  data?.map((item: any) => {
    const eachValue: RiskStratificationCumulative = {
      tier: '',
      count: 0,
      memberIds: [],
      handleClick: () => {},
    };
    eachValue.tier = item._id;
    eachValue.count = item.count;
    eachValue.memberIds = item.member_unique_id_list;
    eachValue.handleClick = () => {
      const memberIds = item.member_unique_id_list;
      storeMemberIds(memberIds, eachValue.tier, 'Cost');
    };
    riskCost.push(eachValue);
  });

  return riskCost;
};

export const formatRiskUtilization = (data: any, storeMemberIds?: any) => {
  const riskUtilization: RiskStratificationCumulative[] = [];

  data?.map((item: any) => {
    const eachValue: RiskStratificationCumulative = {
      tier: '',
      count: 0,
      memberIds: [],
      handleClick: () => {},
    };
    eachValue.tier = item._id;
    eachValue.count = item.utilization_count;
    eachValue.memberIds = item.member_unique_id_list;
    eachValue.handleClick = () => {
      const memberIds = item.member_unique_id_list;
      storeMemberIds(memberIds, eachValue.tier, 'Utilization');
    };
    riskUtilization.push(eachValue);
  });

  return riskUtilization;
};

export const formatTriggerConditions = (data: any, storeMemberIds?: any) => {
  const riskTriggerConditions: RiskStratificationCumulative[] = [];

  data?.map((item: any) => {
    const eachValue: RiskStratificationCumulative = {
      tier: '',
      count: 0,
      memberIds: [],
      handleClick: () => {},
    };
    eachValue.tier = item._id;
    eachValue.count = item.total_trigger_count;
    eachValue.memberIds = item.member_unique_id_list;
    eachValue.handleClick = () => {
      const memberIds = item.member_unique_id_list;
      storeMemberIds(memberIds, eachValue.tier, 'Trigger Conditions');
    };
    riskTriggerConditions.push(eachValue);
  });

  return riskTriggerConditions;
};

export const formatRiskConsumption = (data: any, storeMemberIds?: any) => {
  const riskConsumption: RiskStratificationCumulative[] = [];

  data?.map((item: any) => {
    const eachValue: RiskStratificationCumulative = {
      tier: '',
      count: 0,
      memberIds: [],
      handleClick: () => {},
    };
    eachValue.tier = item._id;
    eachValue.count = item.count;
    eachValue.memberIds = item.member_unique_id_list;
    eachValue.handleClick = () => {
      const memberIds = item.member_unique_id_list;
      storeMemberIds(memberIds, eachValue.tier, 'Risk Consumption');
    };
    riskConsumption.push(eachValue);
  });

  return riskConsumption;
};

export const formatRiskStratificationBreakDown = (
  riskCost: RiskStratificationCumulative[],
  riskUtilization: RiskStratificationCumulative[],
  riskTriggerConditions: RiskStratificationCumulative[],
  // riskConsumption?: RiskStratificationCumulative[],
) => {
  const riskStratificationData: RiskStratificationChartType = {
    riskStratificationColumns: [
      {
        id: 'C1',
        label: 'Cost',
      },
      {
        id: 'C2',
        label: 'Utilization',
      },
      {
        id: 'C3',
        label: 'Trigger conditions',
      },
      // {
      //   id: 'C4',
      //   label: 'Risk Consumption',
      // },
    ],
    riskStratificationRows: [
      {
        id: 'R1',
        label: 'Highly Complex',
      },
      {
        id: 'R2',
        label: 'High Risk',
      },
      {
        id: 'R3',
        label: 'Rising Risk',
      },
      {
        id: 'R4',
        label: 'Moderate Risk',
      },
      {
        id: 'R5',
        label: 'Low Risk',
      },
    ],
    riskStratificationData: [],
  };

  const riskCostColumnsData = getColumnsData(riskCost, 0);
  const riskUtilizationColumnsData = getColumnsData(riskUtilization, 1);
  const riskTriggerConditionsColumnsData = getColumnsData(
    riskTriggerConditions,
    2,
  );
  // const riskConsumptionColumns = getColumnsData(riskConsumption ?? [], 3);

  const finalColumnsData = riskCostColumnsData?.concat(
    riskUtilizationColumnsData,
    riskTriggerConditionsColumnsData,
    // riskConsumptionColumns,
  );

  riskStratificationData.riskStratificationData = finalColumnsData;

  return riskStratificationData;
};

const getColumnsData = (
  data: RiskStratificationCumulative[],
  columnIndex: number,
) => {
  const columns = ['C1', 'C2', 'C3', 'C4', 'C5'];
  const rows = ['R1', 'R2', 'R3', 'R4', 'R5'];
  const brLabels = [
    ['1%', '2-5%', '6-20%', '21-60%', '>60%'],
    ['>=4', '3', '2', '1', '0'],
    ['>=5', '4', '3', '1-2', '0'],
    ['>=5', '4', '3', '2', '0-1'],
    ['>=6', '5', '3-4', '1-2', '0'],
  ];
  const visitedRows: string[] = [];

  const getTierLabel = (label: string) => {
    switch (label) {
      case 'tier1':
        return 'Tier 1';
      case 'tier2':
        return 'Tier 2';
      case 'tier3':
        return 'Tier 3';
      case 'tier4':
        return 'Tier 4';
      case 'tier5':
        return 'Tier 5';
      default:
        return label;
    }
  };
  const tempArray: RiskStratificationDataType[] = [];
  data?.map((item: any) => {
    switch (getTierLabel(item?.tier)) {
      case 'Tier 1':
        tempArray.push({
          rowid: rows[0],
          columnid: columns[columnIndex],
          displayvalue: `<u>${item.count}</u>`,
          colorrangelabel: rows[0],
          uniqueMemberIds: item.memberIds,
          brLabel: brLabels[columnIndex][0],
          handleClick: item.handleClick,
          link: ' ',
        });
        visitedRows.push(rows[0]);
        break;
      case 'Tier 2':
        tempArray.push({
          rowid: rows[1],
          columnid: columns[columnIndex],
          displayvalue: `<u>${item.count}</u>`,
          colorrangelabel: rows[1],
          uniqueMemberIds: item.memberIds,
          brLabel: brLabels[columnIndex][1],
          handleClick: item.handleClick,
          link: ' ',
        });
        visitedRows.push(rows[1]);
        break;
      case 'Tier 3':
        tempArray.push({
          rowid: rows[2],
          columnid: columns[columnIndex],
          displayvalue: `<u>${item.count}</u>`,
          colorrangelabel: rows[2],
          uniqueMemberIds: item.memberIds,
          brLabel: brLabels[columnIndex][2],
          handleClick: item.handleClick,
          link: ' ',
        });
        visitedRows.push(rows[2]);
        break;
      case 'Tier 4':
        tempArray.push({
          rowid: rows[3],
          columnid: columns[columnIndex],
          displayvalue: `<u>${item.count}</u>`,
          colorrangelabel: rows[3],
          uniqueMemberIds: item.memberIds,
          brLabel: brLabels[columnIndex][3],
          handleClick: item.handleClick,
          link: ' ',
        });
        visitedRows.push(rows[3]);
        break;
      case 'Tier 5':
        tempArray.push({
          rowid: rows[4],
          columnid: columns[columnIndex],
          displayvalue: `<u>${item.count}</u>`,
          colorrangelabel: rows[4],
          uniqueMemberIds: item.memberIds,
          brLabel: brLabels[columnIndex][4],
          handleClick: item.handleClick,
          link: ' ',
        });
        visitedRows.push(rows[4]);
        break;
      default:
        tempArray.push({
          rowid: '',
          columnid: '',
          displayvalue: `<u>0</u>`,
          colorrangelabel: '',
          uniqueMemberIds: [],
          brLabel: '',
          handleClick: () => {},
        });
        break;
    }
  });

  const unVisitedRows = rows?.filter((item) => !visitedRows?.includes(item));

  unVisitedRows?.map((item) => {
    tempArray.push({
      rowid: item,
      columnid: columns[columnIndex],
      displayvalue: `<u>0</u>`,
      colorrangelabel: item,
      uniqueMemberIds: [],
      brLabel: brLabels[columnIndex][rows?.indexOf(item)],
      handleClick: () => {},
    });
  });

  return tempArray;
};

export const formatRiskIndex = (data: any) => {
  const riskIndexData: RiskIndex = {
    currentYearIndex:
      data.overall_risk_index !== 'NA'
        ? data.overall_risk_index?.toFixed(2)
        : 'NA',
    previousYearIndex: data?.previous_year_risk_index,
    riskPercentage: Math.abs(roundNumber(data?.percentage, 2)),
    increment: data?.percentage > 0,
    riskStatus: data?.risk_status,
  };

  return riskIndexData;
};
export const formatMedicalLossRatio = (data: any) => {
  const medicalLossRatioData: MedicalLossRatio = {
    current_year_mlr:
      data.current_year_mlr === null
        ? 'NO DATA'
        : roundNumber(data.current_year_mlr, 2) + '%',
    previous_year_mlr:
      data.previous_year_mlr === null
        ? 'NO DATA'
        : roundNumber(data.previous_year_mlr, 2),
    percentage: Math.abs(roundNumber(data.percentage, 2)),
    increment: data.percentage > 0,
  };

  return medicalLossRatioData;
};

export const formatCareQuality = (data: any) => {
  const careQualityData: CareQualityData = {
    data: [
      {
        label: 'Good Performing',
        value: data?.count_of_good_performing,
        color: patientAccessTheme.palette.status.success!,
      },
      {
        label: 'Average Performing',
        value: data?.count_of_average_performing,
        color: patientAccessTheme.palette.primary[400]!,
      },
      {
        label: 'Poor Performing',
        value: data?.count_of_poor_performing,
        color: patientAccessTheme.palette.status.error!,
      },
      {
        label: 'Non Eligible',
        value: data?.count_of_non_eligible,
        color: patientAccessTheme.palette.grey[300]!,
      },
    ],
    centerLabel: (
      Number(data?.count_of_good_performing) +
      Number(data?.count_of_average_performing) +
      Number(data?.count_of_poor_performing) +
      Number(data?.count_of_non_eligible)
    )?.toString(),
  };

  return careQualityData;
};

export const formatCostTrend = (data: any): CostTrendChartType => {
  const result: CostTrendChartType = {
    medicalClaimsData: [],
    medicalTotalPaid: [],
    medicalPlanPaid: [],
    medicalDeductible: [],
    medicalCopay: [],
    medicalCoinsurance: [],

    rxClaims: [],
    rxTotalPaid: [],
    rxPlanPaid: [],
    rxDeductible: [],
    rxCopay: [],
    rxCoinsurance: [],

    xAxisValues: [],
  };

  const monthArray: string[] = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  data?.forEach((item: any) => {
    const baseMonthLabel = {
      label: `${monthArray[item.month - 1]} <br/> ${item.year}`,
    };

    const commonData = {
      month: item.month,
      value: item.total_cost,
    };

    const commonClaims = {
      month: item.month,
      value: item.total_claims,
    };

    const planPaid = {
      month: item.month,
      value: item.total_plan_paid ?? '',
    };

    const deductible = {
      month: item.month,
      value: item.total_deductible ?? '',
    };

    const copay = {
      month: item.month,
      value: item.total_copay ?? '',
    };

    const coinsurance = {
      month: item.month,
      value: item.total_coinsurance ?? '',
    };

    if (item.claim_type === 'rx') {
      result.rxTotalPaid.push(commonData);
      result.rxClaims.push(commonClaims);
      result.rxPlanPaid.push(planPaid);
      result.rxDeductible.push(deductible);
      result.rxCopay.push(copay);
      result.rxCoinsurance.push(coinsurance);
    }

    if (item.claim_type === 'medical') {
      result.medicalTotalPaid.push(commonData);
      result.medicalClaimsData.push(commonClaims);
      result.medicalPlanPaid.push(planPaid);
      result.medicalDeductible.push(deductible);
      result.medicalCopay.push(copay);
      result.medicalCoinsurance.push(coinsurance);
    }

    // Push x-axis label only once per month
    if (!result?.xAxisValues?.find((x) => x.label === baseMonthLabel.label)) {
      result?.xAxisValues?.push(baseMonthLabel);
    }
  });

  return result;
};

export const formatHighCostClaimants = (
  data: any,
  fractionDigits: number = 2,
) => {
  const totalCost = numFormatterWithSuffix(Number(data?.totalCost), 2);

  const getToolText = (value: number) => {
    return `<div style="background-color:#33475B;padding: 8px;"><div style="color:#FFFFFF;font-size:13px;font-weight:500;" ><span>${numberToCurrencyFormatter(
      value,
      true,
    )}</span></div>`;
  };
  const highCostClaimantsData: CareQualityData = {
    data: [
      {
        label: 'Top 10% population',
        value: data?.topTenPercentPaid?.toFixed(fractionDigits),
        color: patientAccessTheme.palette.riskStratificationCard?.row2Color!,
        toolText: getToolText(data?.topTenPercentPaid),
      },
      {
        label: 'Middle 40% population',
        value: data?.middleFourtyPercentPaid?.toFixed(fractionDigits),
        color: '#87A6FB',
        toolText: getToolText(data?.middleFourtyPercentPaid),
      },
      {
        label: 'Bottom 50% population',
        value: data?.bottomFiftyPercentPaid?.toFixed(fractionDigits),
        color: patientAccessTheme.palette.riskStratificationCard?.row1Color!,
        toolText: getToolText(data?.bottomFiftyPercentPaid),
      },
    ],
    centerLabel: '$' + totalCost,
  };

  return highCostClaimantsData;
};

export const formatDiseasePrevalence = (
  data: any,
  type: 'cost' | 'member',
  limit: number,
) => {
  const diseasePrevalence: TitleValueType[] = [];

  data?.map((item: any) => {
    const eachValue: TitleValueType = {
      title: '',
      value: 0,
    };
    if (item.chronicDiseaseName !== '') {
      eachValue.title = item.chronicDiseaseName;
      eachValue.value =
        type === 'cost'
          ? item.chronicDiseasePercent.toFixed(0)
          : item.chronicDiseasePercent.toFixed(0);
      diseasePrevalence.push(eachValue);
    }
  });

  return diseasePrevalence?.slice(0, limit);
};

export const formatUtilization = (
  data: any,
  type: 'cost' | 'member',
  limit: number,
) => {
  const utilization: TitleValueType[] = [];

  data?.map((item: any) => {
    const eachValue: TitleValueType = {
      title: '',
      value: 0,
    };
    if (item.typeOfService !== '') {
      eachValue.title = item.typeOfService;
      eachValue.value =
        item.typeOfServicePercent >= 100
          ? 80.59
          : item.typeOfServicePercent.toFixed(0);
      utilization.push(eachValue);
    }
  });

  return utilization?.slice(0, limit);
};

export const formatPharmacyUsage = (data: any, type: 'cost' | 'member') => {
  const pharmacyData: TitleValueType[] = [];

  data?.map((item: any) => {
    const eachValue: TitleValueType = {
      title: '',
      value: 0,
    };
    if (item.medication !== '') {
      eachValue.title = item.medication;
      eachValue.value =
        type === 'cost'
          ? item.totalCostRatio.toFixed(2)
          : item.totalMembersRatio.toFixed(2);
      pharmacyData.push(eachValue);
    }
  });

  return pharmacyData?.slice(0, 5);
};

export const formatUtilizationPer100 = (data: any, isDottedText?: boolean) => {
  const colorsArray: any[] = [
    '#08C7B4',
    patientAccessTheme.palette.warning[500],
    patientAccessTheme.palette.primary[300],
    patientAccessTheme.palette.riskStratificationCard?.row2Color!,
    patientAccessTheme.palette.riskStratificationCard?.row1Color!,
  ];

  const chartData = [];
  const labelsData = [];
  for (let i = 0; i < data.length; i++) {
    chartData.push({
      value: data[i].usageRatio,
      label: data[i].typeOfService,
      color: colorsArray[i],
      toolText: `<div style="background-color:#33475B;padding: 8px;"><div style="color:#FFFFFF;font-size:13px;font-weight:500;" ><span>${data[
        i
      ].usageRatio.toFixed(2)}%</span></div>`,
    });
    labelsData.push(
      getLegendItem(colorsArray[i], data[i].typeOfService, isDottedText),
    );
  }

  return {
    chartData: chartData,
    labelsData: labelsData,
  };
};

export const getPmpmForCohorts = (
  pmpm: any,
  claim_id_list: string[],
  member_unique_id_list: string[],
) => {
  if (claim_id_list.length > 0 && member_unique_id_list.length > 0) {
    return '--';
  } else {
    return valueWithPercentage(
      pmpm?.pmpmPercentage,
      pmpm?.increment,
      pmpm?.increment
        ? patientAccessTheme.palette.status.error
        : patientAccessTheme.palette.success.greenbg,
    );
  }
};

export const getPmpmValueForCohorts = (
  pmpm: any,
  claim_id_list: string[],
  member_unique_id_list: string[],
) => {
  if (claim_id_list.length > 0 && member_unique_id_list.length > 0) {
    return '--';
  } else {
    return pmpm?.pmpm;
  }
};

export const getPepmForCohorts = (
  pepm: any,
  claim_id_list: string[],
  member_unique_id_list: string[],
) => {
  if (claim_id_list.length > 0 && member_unique_id_list.length > 0) {
    return '--';
  } else {
    return valueWithPercentage(
      pepm?.pepmPercentage,
      pepm?.increment,
      pepm?.increment
        ? patientAccessTheme.palette.status.error
        : patientAccessTheme.palette.success.greenbg,
    );
  }
};

export const getPepmValueForCohorts = (
  pepm: any,
  claim_id_list: string[],
  member_unique_id_list: string[],
) => {
  if (claim_id_list.length > 0 && member_unique_id_list.length > 0) {
    return '--';
  } else {
    return pepm?.pepm;
  }
};
